import React, { useContext, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';

import clsx from 'clsx';

import {
  ListItem,
  Button,
  Collapse,
  Stack,
  IconButton,
  makeStyles,
  alpha,
  ExpandLessIcon as ExpandLess,
  ExpandMoreIcon as ExpandMore,
  StarIcon as Start,
  StarBorderIcon as StarBorder,
  OpenInNewIcon,
  CircularProgress,
} from '@gogame/components';
import { useProcessAsync } from '@gogame/hooks-ts';

import { useScreenLink } from '../ScreenLink';
import FavoritesContext from './FavoritesContext';
import { useUpdatePosition } from './_updatePopoverPosition';

const useStyles = makeStyles((theme) => ({
  nestedButtonLeaf: {
    textTransform: 'none',
  },
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
    '& .focus-button': {
      display: 'none',
    },
    '&:hover .focus-button': {
      display: 'flex',
    },
    '&:hover .button-leaf': {
      backgroundColor: alpha(
        theme.palette.text.primary,
        theme.palette.action.hoverOpacity
      ),
    },
  },
  button: {
    padding: '12px 16px',
    letterSpacing: 0,
    textTransform: 'uppercase',
    justifyContent: 'space-between',
    fontWeight: theme.typography.fontWeightRegular,
  },
  buttonLeaf: {
    padding: '12px 16px',
    letterSpacing: 0,
    justifyContent: 'space-between',
    fontSize: '13px',
    height: '48px',
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    borderRadius: 0,
  },
  active: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  activeLeaf: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    // pointerEvents: 'none',
    background: alpha(
      theme.palette.primary.main,
      theme.palette.action.activatedOpacity
    ),
  },
}));

const NavigationItem = ({
  link,
  externalLink,
  title,
  id,
  linkable,
  children,
  component,
  level,
  initialOpen,
  onClose,
}) => {
  const location = useLocation();
  const to = useScreenLink({
    link,
  });
  const classes = useStyles();
  const [loading, processAsync] = useProcessAsync();

  const locationPathname = location.pathname.toLowerCase();
  const toPathname = to?.pathname?.toLowerCase();

  const activeSection = locationPathname.indexOf(`${toPathname}`) === 0;
  const [open, setOpen] = React.useState(initialOpen || activeSection);
  const { withFavorite, isFavorite, toggle } = useContext(FavoritesContext);

  const handleToggle = () => setOpen((o) => !o);
  const updatedPosition = useUpdatePosition();

  useEffect(() => {
    if (initialOpen) {
      // open all searchable nav items
      setOpen(true);
    } else if (!initialOpen && !activeSection) {
      // close all nav items except active
      setOpen(false);
    }
  }, [initialOpen]);

  if (linkable) {
    const favorite = isFavorite(id);
    const handleToggleFavorite = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return processAsync(toggle(id));
    };

    return (
      <ListItem
        disableGutters
        className={classes.itemLeaf}
        component={component}
      >
        <Button
          color={'inherit'}
          component={!externalLink ? Link : 'a'}
          fullWidth={true}
          className={clsx(classes.buttonLeaf, 'button-leaf', {
            [classes.activeLeaf]: activeSection,
            [classes.nestedButtonLeaf]: level !== 0,
          })}
          to={!externalLink ? to : null}
          disableTouchRipple
          style={{
            paddingRight: withFavorite && id ? '46px' : '16px',
          }}
          onClick={onClose}
          href={externalLink}
          target={externalLink ? '_blank' : null}
        >
          <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Stack>{title}</Stack>
            {externalLink ? (
              <OpenInNewIcon color={'action'} sx={{ fontSize: 16 }} />
            ) : null}
          </Stack>
        </Button>
        {withFavorite && id ? (
          loading ? (
            <CircularProgress
              size={24}
              style={{
                position: 'absolute',
                right: '8px',
              }}
            />
          ) : (
            <IconButton
              className={'focus-button'}
              onClick={handleToggleFavorite}
              size={'medium'}
              WrapperProps={{
                style: {
                  position: 'absolute',
                  right: '0',
                },
              }}
              tooltip={favorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              {favorite ? <Start /> : <StarBorder />}
            </IconButton>
          )
        ) : null}
      </ListItem>
    );
  }

  return (
    <ListItem
      disableGutters
      className={classes.item}
      ContainerComponent={component}
    >
      <Button
        color={'inherit'}
        className={clsx(classes.button, {
          [classes.active]: activeSection,
        })}
        disableTouchRipple
        onClick={handleToggle}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        fullWidth={true}
      >
        {title}
      </Button>
      <Collapse
        in={open}
        timeout={initialOpen ? 0 : 'auto'}
        unmountOnExit
        onExited={updatedPosition}
        onEntered={updatedPosition}
      >
        {children}
      </Collapse>
    </ListItem>
  );
};

export default NavigationItem;
